import { contents } from "@/mocks/content";
import type { ContentItem, ContentType } from "@/types/content";

export const typeLabels: Record<ContentType, string> = {
  video: "趣味视频",
  joke: "搞笑段子",
  interview: "轻松访谈",
};

export const typeRouteMap: Record<ContentType, string> = {
  video: "video",
  joke: "joke",
  interview: "interview",
};

export function isContentType(value: string | undefined): value is ContentType {
  return value === "video" || value === "joke" || value === "interview";
}

export function getContentById(id: string | undefined): ContentItem | undefined {
  return contents.find((item) => item.id === id);
}

export function getContentsByType(type: ContentType): ContentItem[] {
  return contents.filter((item) => item.type === type);
}

export function getLatestContents(limit = 8): ContentItem[] {
  return [...contents]
    .sort((a, b) => (a.date < b.date ? 1 : -1))
    .slice(0, limit);
}

export function getHotContents(limit = 10): ContentItem[] {
  return [...contents].sort((a, b) => b.views - a.views).slice(0, limit);
}

export function getFeaturedCarousel(limit = 5): ContentItem[] {
  return [...contents].sort((a, b) => b.likes - a.likes).slice(0, limit);
}

export function getRelatedContents(item: ContentItem, limit = 6): ContentItem[] {
  const sameType = contents.filter((c) => c.type === item.type && c.id !== item.id);
  const byTag = sameType.filter((c) => c.tags.some((t) => item.tags.includes(t)));
  const rest = sameType.filter((c) => !byTag.includes(c));
  return [...byTag, ...rest].slice(0, limit);
}

export function searchContents(keyword: string, type?: ContentType | "all"): ContentItem[] {
  const kw = keyword.trim().toLowerCase();
  const pool = type && type !== "all" ? contents.filter((c) => c.type === type) : contents;
  if (!kw) return pool;
  return pool.filter((item) => {
    const haystack = [item.title, item.summary, item.body, item.author, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function filterContents(
  list: ContentItem[],
  options: { duration?: "all" | "short" | "medium" | "long"; year?: string; tag?: string },
): ContentItem[] {
  return list.filter((item) => {
    if (options.duration && options.duration !== "all") {
      if (item.type !== "video") return false;
      if (options.duration === "short" && item.duration >= 180) return false;
      if (options.duration === "medium" && (item.duration < 180 || item.duration > 300)) return false;
      if (options.duration === "long" && item.duration <= 300) return false;
    }
    if (options.year && options.year !== "all" && String(item.year) !== options.year) return false;
    if (options.tag && options.tag !== "all" && !item.tags.includes(options.tag)) return false;
    return true;
  });
}

export function sortContents(list: ContentItem[], sort: "latest" | "hot"): ContentItem[] {
  const copy = [...list];
  if (sort === "hot") return copy.sort((a, b) => b.views - a.views);
  return copy.sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function formatDuration(seconds: number): string {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

export function formatCount(value: number): string {
  if (value >= 10000) return `${(value / 10000).toFixed(1)}万`;
  return String(value);
}

export function collectTags(type: ContentType): string[] {
  const set = new Set<string>();
  getContentsByType(type).forEach((item) => item.tags.forEach((t) => set.add(t)));
  return Array.from(set);
}

export function collectYears(type: ContentType): string[] {
  const set = new Set<number>();
  getContentsByType(type).forEach((item) => set.add(item.year));
  return Array.from(set)
    .sort((a, b) => b - a)
    .map(String);
}